import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Chip,
  Alert,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  TablePagination,
  Tabs,
  Tab,
  Stack,
  Divider,
  Tooltip,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
  Modal as MuiModal,
  Avatar,
} from "@mui/material";
import {
  Search,
  Refresh,
  AssignmentLate,
  CalendarToday,
  CheckCircle,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  Close as CloseIcon,
  LocalShipping as VehicleIcon,
  Description,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  vehicleList,
  getVehicleDashboardStats,
  getDocumentAlertsSummary,
} from "../../../Services/Services";
import { useNavigate } from "react-router-dom";

import { T } from "../../../theme";

// ── Style Fragments ────────────────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 34,
    ...sq,
    bgcolor: T.white,
    transition: "box-shadow 0.2s",
  },
  "& fieldset": { border: `1.5px solid ${T.border}` },
  "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
  "& .Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
  "& .Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const STATUS_CONFIG = {
  expired: {
    color: T.red,
    bg: T.redBg,
    label: "Expired",
    icon: <ErrorIcon sx={{ fontSize: 12 }} />,
  },
  missing: {
    color: T.red,
    bg: T.redBg,
    label: "Missing",
    icon: <ErrorIcon sx={{ fontSize: 12 }} />,
  },
  critical: {
    color: T.red,
    bg: T.redBg,
    label: "Critical",
    icon: <ErrorIcon sx={{ fontSize: 12 }} />,
  },
  warning: {
    color: T.amber,
    bg: T.amberBg,
    label: "Warning",
    icon: <WarningIcon sx={{ fontSize: 12 }} />,
  },
  good: {
    color: T.green,
    bg: T.greenBg,
    label: "Good",
    icon: <CheckCircle sx={{ fontSize: 12 }} />,
  },
};

// ── Shared: DialogHeader ───────────────────────────────────────────
const DialogHeader = ({ title, subtitle, onClose, bgColor, icon: Icon }) => (
  <Box
    sx={{
      background:
        bgColor ||
        `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {Icon && <Icon sx={{ color: T.white, fontSize: 18 }} />}
      <Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: T.white,
            fontSize: "0.88rem",
            letterSpacing: "0.01em",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.7rem" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
    <IconButton
      onClick={onClose}
      size="small"
      sx={{
        color: "rgba(255,255,255,0.8)",
        p: 0.5,
        ...sq,
        "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
      }}
    >
      <CloseIcon sx={{ fontSize: 17 }} />
    </IconButton>
  </Box>
);

// ── Shared: ActionButton ───────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete = false,
  isEdit = false,
  isView = false,
  onClick,
}) => {
  const color = isDelete
    ? T.red
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : T.primary;
  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : T.primaryBg;
  const border = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : T.border;
  const hoverBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── Status Chip Helper ─────────────────────────────────────────────
const getStatusChip = (status, days) => {
  if (status === "expired" || status === "missing") {
    return (
      <Chip
        label="Expired"
        size="small"
        sx={{
          bgcolor: T.redBg,
          color: T.red,
          ...sq,
          fontSize: "0.58rem",
          height: 18,
          border: `1px solid ${T.redBorder}`,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  }
  if (status === "expiring") {
    const isUrgent = days <= 7;
    return (
      <Chip
        label={`${days}d left`}
        size="small"
        sx={{
          bgcolor: isUrgent ? T.redBg : T.amberBg,
          color: isUrgent ? T.red : T.amber,
          ...sq,
          fontSize: "0.58rem",
          height: 18,
          border: `1px solid ${isUrgent ? T.redBorder : T.amber + "44"}`,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  }
  return (
    <Chip
      label="Valid"
      size="small"
      sx={{
        bgcolor: T.greenBg,
        color: T.green,
        ...sq,
        fontSize: "0.58rem",
        height: 18,
        border: "1px solid rgba(16,185,129,0.2)",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, subtext, color, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 1.5,
      ...sq,
      position: 'relative',
      overflow: 'hidden',
      bgcolor: `${color}12`,
      border: `1px solid ${color}40`,
      boxShadow: 'none',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
      '&:hover': onClick
        ? {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 15px ${color}25`,
            borderColor: color,
          }
        : {},
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -20,
        right: -20,
        width: 60,
        height: 60,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        zIndex: 0,
      }}
    />
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      <Typography
        sx={{
          fontSize: '0.65rem',
          color: T.textSub,
          mb: 0.5,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: color, lineHeight: 1.1 }}>
        {value || 0}
      </Typography>
      {subtext && (
        <Typography
          sx={{
            fontSize: '0.6rem',
            color: T.textMuted,
            mt: 0.5,
            fontWeight: 600,
          }}
        >
          {subtext}
        </Typography>
      )}
    </Box>
  </Paper>
);

// ── DocumentAlertsModal ────────────────────────────────────────────
const DocumentAlertsModal = ({ open, onClose, title, documents, type }) => {
  const [searchTerm, setSearchTerm] = useState("");
  if (!open) return null;

  const isExpired = type === "expired";
  const headerColor = isExpired
    ? `linear-gradient(135deg, ${T.red} 0%, #ef4444 100%)`
    : `linear-gradient(135deg, ${T.amber} 0%, #f59e0b 100%)`;

  const filteredDocs = documents.filter((doc) =>
    `${doc.vehicle_number} ${doc.document_name || doc.docName} ${doc.chassis_number}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase()),
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return (
    <MuiModal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", sm: "80%", md: "70%" },
          maxHeight: "85vh",
          bgcolor: T.white,
          ...sq,
          border: `1.5px solid ${T.border}`,
          boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <DialogHeader
          title={`${title} (${filteredDocs.length})`}
          onClose={onClose}
          bgColor={headerColor}
          icon={isExpired ? ErrorIcon : WarningIcon}
        />

        {/* Search */}
        <Box
          sx={{
            p: 1.5,
            borderBottom: `1px solid ${T.border}`,
            bgcolor: T.white,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Search by vehicle number, document type or chassis…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 15, color: T.primary }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm("")}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflow: "auto", p: 0 }}>
          {filteredDocs.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CheckCircle sx={{ fontSize: 44, color: T.border, mb: 1 }} />
              <Typography
                sx={{ color: T.textSub, fontSize: "0.8rem", fontWeight: 600 }}
              >
                No {isExpired ? "expired" : "expiring"} documents found
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="small"
                stickyHeader
                sx={{
                  "& td, & th": {
                    py: 0,
                    px: 1,
                    fontSize: "0.75rem",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                  },
                  "& th": {
                    fontWeight: 700,
                    color: T.headerText,
                    bgcolor: `${T.headerBg} !important`,
                    py: 0.2,
                    fontSize: "0.8rem",
                    letterSpacing: "0.01em",
                    borderBottom: `1px solid #ccc`,
                    lineHeight: 1.2,
                    height: 28,
                  },
                  "& td": {
                    height: 26,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "Vehicle No.",
                      "Chassis No.",
                      "Document Type",
                      "Expiry Date",
                      "Status",
                      "Days Left",
                    ].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          bgcolor: `${T.headerBg} !important`,
                          color: `${T.headerText} !important`,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDocs.map((doc, idx) => {
                    const expiryDate = doc.expiry_date || doc.expiryDate;
                    const dateObj = expiryDate ? new Date(expiryDate) : null;
                    const expired = dateObj && dateObj < today;
                    const daysLeft = dateObj
                      ? Math.ceil((dateObj - today) / (1000 * 60 * 60 * 24))
                      : 0;
                    const docName = doc.document_name || doc.docName;
                    const rowBg = idx % 2 === 0 ? T.baseRow : T.altRow;
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          "& > td": { bgcolor: rowBg },
                          "&:hover > td": {
                            bgcolor: `${T.rowHover} !important`,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: T.primary,
                            }}
                          >
                            {doc.vehicle_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                              color: T.text,
                            }}
                          >
                            {doc.chassis_number || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              color: T.text,
                              fontWeight: 500,
                            }}
                          >
                            {docName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatDate(expiryDate)}
                            size="small"
                            sx={{
                              fontSize: "0.58rem",
                              height: 16,
                              ...sq,
                              bgcolor: T.white,
                              color: T.primary,
                              border: `1px solid ${T.border}`,
                              fontWeight: 700,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={expired ? "Expired" : "Expiring Soon"}
                            size="small"
                            sx={{
                              bgcolor: expired ? T.redBg : T.amberBg,
                              color: expired ? T.red : T.amber,
                              ...sq,
                              fontSize: "0.55rem",
                              height: 16,
                              border: `1px solid ${expired ? T.redBorder : T.amber + "44"}`,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: expired
                                ? T.red
                                : daysLeft <= 7
                                  ? T.red
                                  : T.amber,
                            }}
                          >
                            {expired ? "Expired" : `${daysLeft} days`}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            borderTop: `1px solid ${T.border}`,
            px: 2.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: T.white,
          }}
        >
          <Typography
            sx={{ fontSize: "0.65rem", color: T.textMuted, fontWeight: 500 }}
          >
            {filteredDocs.length} document{filteredDocs.length !== 1 ? "s" : ""}{" "}
            found
          </Typography>
          <Button
            size="small"
            onClick={onClose}
            sx={
              isExpired
                ? { ...dangerBtn, height: 30, px: 2 }
                : {
                    ...sq,
                    bgcolor: T.amberBg,
                    color: T.amber,
                    border: `1.5px solid ${T.amber}44`,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    textTransform: "none",
                    height: 30,
                    px: 2,
                    "&:hover": { bgcolor: "#fef3c7", borderColor: T.amber },
                  }
            }
          >
            Close
          </Button>
        </Box>
      </Box>
    </MuiModal>
  );
};

// ── MobileVehicleAlertCard ─────────────────────────────────────────
const MobileVehicleAlertCard = ({ vehicle, expanded, onToggle, onUpdate }) => {
  const statusConfig = STATUS_CONFIG[vehicle.alertStatus] || STATUS_CONFIG.good;
  const issueCount = vehicle.documentStatuses.filter(
    (d) => d.status !== "valid",
  ).length;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        bgcolor: T.white,
        overflow: "hidden",
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      {/* Top Row */}
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        {/* Icon Block */}
        <Box
          sx={{
            width: 60,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            flexShrink: 0,
          }}
        >
          <VehicleIcon
            sx={{ fontSize: 26, color: expanded ? T.white : T.primary }}
          />
        </Box>

        {/* Info */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {vehicle.vehicle_number}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textMuted,
              fontFamily: "monospace",
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {vehicle.chassis_number || "—"}
          </Typography>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.4 }}
          >
            <Chip
              icon={statusConfig.icon}
              label={statusConfig.label}
              size="small"
              sx={{
                fontSize: "0.55rem",
                height: 18,
                ...sq,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
                border: `1px solid ${statusConfig.color}33`,
                "& .MuiChip-icon": { color: statusConfig.color, ml: 0.5 },
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
            {issueCount > 0 && (
              <Chip
                label={`${issueCount} issue${issueCount !== 1 ? "s" : ""}`}
                size="small"
                sx={{
                  fontSize: "0.55rem",
                  height: 18,
                  ...sq,
                  bgcolor: T.redBg,
                  color: T.red,
                  border: `1px solid ${T.redBorder}`,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", pr: 1.5 }}>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 28,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {/* Expanded Panel */}
      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.primary,
              mb: 1,
              fontSize: "0.68rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Description sx={{ fontSize: 13 }} /> Document Status
          </Typography>
          <Stack spacing={0.5}>
            {vehicle.documentStatuses?.map((doc, idx) => {
              const borderColor =
                doc.status === "expired" || doc.status === "missing"
                  ? T.red
                  : doc.status === "expiring"
                    ? doc.daysRemaining <= 7
                      ? T.red
                      : T.amber
                    : T.green;
              return (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1,
                    py: 0.75,
                    bgcolor: T.white,
                    ...sq,
                    border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${borderColor}`,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: T.text,
                      }}
                    >
                      {doc.name}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.62rem", color: T.textMuted }}
                    >
                      {doc.expiryDate
                        ? formatDate(doc.expiryDate)
                        : "Not uploaded"}
                    </Typography>
                  </Box>
                  {getStatusChip(doc.status, doc.daysRemaining)}
                </Box>
              );
            })}
          </Stack>
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onUpdate(vehicle)}
              sx={{ ...primaryBtn, height: 30, px: 1.5 }}
            >
              Update Docs
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── DocumentDetailsModal ───────────────────────────────────────────
const DocumentDetailsModal = ({ open, onClose, vehicle, onUpdate }) => {
  if (!vehicle) return null;
  const statusConfig = STATUS_CONFIG[vehicle.alertStatus] || STATUS_CONFIG.good;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          ...sq,
          border: `1.5px solid ${T.border}`,
          boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <DialogHeader
        title="Document Details"
        onClose={onClose}
        icon={AssignmentLate}
      />

      <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
        {/* Vehicle Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            pb: 2,
            borderBottom: `1px solid ${T.border}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar
              sx={{
                width: 40,
                height: 40,
                ...sq,
                bgcolor: T.primaryLight,
                color: T.primary,
                border: `1.5px solid ${T.border}`,
              }}
            >
              <VehicleIcon sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography
                sx={{ fontWeight: 800, fontSize: "0.9rem", color: T.text }}
              >
                {vehicle.vehicle_number}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  color: T.textMuted,
                  fontFamily: "monospace",
                }}
              >
                Chassis: {vehicle.chassis_number || "—"}
              </Typography>
            </Box>
          </Stack>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="small"
            sx={{
              bgcolor: statusConfig.bg,
              color: statusConfig.color,
              ...sq,
              fontSize: "0.62rem",
              height: 20,
              border: `1px solid ${statusConfig.color}33`,
              "& .MuiChip-icon": { color: statusConfig.color },
            }}
          />
        </Box>

        {/* Section Label */}
        <Typography
          sx={{
            fontWeight: 700,
            color: T.primary,
            mb: 1.25,
            fontSize: "0.68rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <CalendarToday sx={{ fontSize: 13 }} /> Document Details
        </Typography>

        {/* Document Cards */}
        <Grid container spacing={1}>
          {vehicle.documentStatuses?.map((doc, idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  bgcolor: T.white,
                  transition: "border-color 0.2s",
                  "&:hover": { borderColor: T.borderHover },
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.75}
                >
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "0.72rem", color: T.text }}
                  >
                    {doc.name}
                  </Typography>
                  {getStatusChip(doc.status, doc.daysRemaining)}
                </Stack>
                <Typography
                  sx={{
                    fontSize: "0.6rem",
                    color: T.textMuted,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  Expiry Date
                </Typography>
                <Typography
                  sx={{ fontSize: "0.75rem", fontWeight: 600, color: T.text }}
                >
                  {formatDate(doc.expiryDate)}
                </Typography>
                {doc.daysRemaining > 0 && doc.daysRemaining <= 30 && (
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      color: doc.daysRemaining <= 7 ? T.red : T.amber,
                      mt: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    {doc.daysRemaining} days remaining
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          gap: 1,
          bgcolor: T.white,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Button
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 32, px: 2 }}
        >
          Close
        </Button>
        <Button
          size="small"
          onClick={() => onUpdate(vehicle)}
          sx={{ ...primaryBtn, height: 32, px: 2.5 }}
        >
          Update Documents
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── DesktopAlertsTable ─────────────────────────────────────────────
const DesktopAlertsTable = ({ vehicles, loading, onViewDetails }) => {
  const columns = [
    { label: "#", align: "center", sticky: true, left: 0, minW: 48 },
    { label: "Vehicle No.", align: "left", sticky: true, left: 48, minW: 140 },
    { label: "Chassis No.", align: "center", minW: 150 },
    { label: "Status", align: "center", minW: 110 },
    { label: "Documents", align: "center", minW: 200 },
    { label: "Actions", align: "center", sticky: true, right: 0, minW: 80 },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sq,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
      }}
    >
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 420px)", overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 640,
            borderCollapse: "collapse",
            "& td, & th": {
              py: 0,
              px: 1.5,
              fontSize: "0.72rem",
              borderBottom: `1px solid ${T.border}`,
            },
            "& th": {
              fontWeight: 700,
              color: T.white,
              bgcolor: `${T.headerBg} !important`,
              py: 0.5,
              fontSize: "0.68rem",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              borderBottom: "none",
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(({ label, align, sticky, left, right, minW }) => (
                <TableCell
                  key={label}
                  align={align}
                  sx={{
                    minWidth: minW,
                    bgcolor: `${T.headerBg} !important`,
                    color: `${T.headerText} !important`,
                    ...(sticky && {
                      position: "sticky",
                      ...(left !== undefined && { left }),
                      ...(right !== undefined && { right }),
                      zIndex: 12,
                    }),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ textAlign: "center", py: 8, border: "none" }}
                >
                  <CircularProgress sx={{ color: T.primary }} size={36} />
                </TableCell>
              </TableRow>
            ) : vehicles.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  sx={{ textAlign: "center", py: 6, border: "none" }}
                >
                  <CheckCircle
                    sx={{
                      fontSize: 40,
                      color: T.border,
                      mb: 1,
                      display: "block",
                      mx: "auto",
                    }}
                  />
                  <Typography
                    sx={{
                      color: T.textSub,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    No document alerts found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map((vehicle, idx) => {
                const isEven = idx % 2 === 0;
                const rowBg = isEven ? T.baseRow : T.altRow;
                const statusConfig =
                  STATUS_CONFIG[vehicle.alertStatus] || STATUS_CONFIG.good;
                const invalidDocs = vehicle.documentStatuses.filter(
                  (d) => d.status !== "valid",
                );

                return (
                  <TableRow
                    key={vehicle.vehicle_id}
                    sx={{
                      "& > td": {
                        bgcolor: rowBg,
                        transition: "background-color 0.12s",
                      },
                      "&:hover > td": {
                        bgcolor: `${T.rowHover} !important`,
                      },
                    }}
                  >
                    {/* # */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: T.textMuted,
                        }}
                      >
                        {idx + 1}
                      </Typography>
                    </TableCell>

                    {/* Vehicle No. */}
                    <TableCell
                      align="left"
                      sx={{
                        position: "sticky",
                        left: 48,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            ...sq,
                            bgcolor: T.primaryLight,
                            color: T.primary,
                            border: `1px solid ${T.border}`,
                            flexShrink: 0,
                          }}
                        >
                          <VehicleIcon sx={{ fontSize: 11 }} />
                        </Avatar>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: T.text,
                          }}
                        >
                          {vehicle.vehicle_number}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Chassis No. */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontFamily: "monospace",
                          color: T.text,
                          fontWeight: 500,
                        }}
                      >
                        {vehicle.chassis_number || "—"}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                      <Chip
                        icon={statusConfig.icon}
                        label={statusConfig.label}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 16,
                          ...sq,
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          border: `1px solid ${statusConfig.color}33`,
                          "& .MuiChip-icon": { color: statusConfig.color },
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </TableCell>

                    {/* Documents */}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        flexWrap="wrap"
                        justifyContent="center"
                      >
                        {invalidDocs.slice(0, 2).map((doc, i) => (
                          <Chip
                            key={i}
                            label={doc.name}
                            size="small"
                            sx={{
                              bgcolor: T.redBg,
                              color: T.red,
                              ...sq,
                              fontSize: "0.55rem",
                              height: 16,
                              border: `1px solid ${T.redBorder}`,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        ))}
                        {invalidDocs.length > 2 && (
                          <Chip
                            label={`+${invalidDocs.length - 2}`}
                            size="small"
                            sx={{
                              fontSize: "0.55rem",
                              height: 16,
                              ...sq,
                              bgcolor: T.white,
                              color: T.primary,
                              border: `1px solid ${T.border}`,
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                      </Stack>
                    </TableCell>

                    {/* Actions */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <ActionButton
                        tip="View Details"
                        icon={<Visibility />}
                        isView
                        onClick={() => onViewDetails(vehicle)}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ── Main Component ─────────────────────────────────────────────────
const DocumentAlert = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [expiringVehicles, setExpiringVehicles] = useState([]);
  const [expiredVehicles, setExpiredVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [stats, setStats] = useState({
    totalExpiring: 0,
    totalExpired: 0,
    criticalExpiring: 0,
  });
  const [alertsModalOpen, setAlertsModalOpen] = useState(false);
  const [alertsModalType, setAlertsModalType] = useState(null);
  const [alertsDocuments, setAlertsDocuments] = useState([]);
  const [expiringDocuments, setExpiringDocuments] = useState([]);
  const [expiredDocuments, setExpiredDocuments] = useState([]);
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));

  useEffect(() => {
    fetchDocumentAlerts();
  }, []);
  useEffect(() => {
    setExpandedCardId(null);
  }, [activeTab, searchTerm, filterType, page, rowsPerPage]);

  const fetchDocumentAlerts = async () => {
    setLoading(true);
    try {
      const summaryResponse = await getDocumentAlertsSummary();
      if (summaryResponse) {
        const expiringDocs = summaryResponse.expiring || [];
        const expiredDocs = summaryResponse.expired || [];
        const s = summaryResponse.stats || {};

        setExpiringDocuments(expiringDocs);
        setExpiredDocuments(expiredDocs);
        setStats({
          totalExpiring: s.total_expiring,
          totalExpired: s.total_expired,
          criticalExpiring: s.critical_count,
        });

        const buildMap = (docs, defaultStatus) => {
          const map = new Map();
          docs.forEach((doc) => {
            if (!map.has(doc.vehicle_id)) {
              map.set(doc.vehicle_id, {
                vehicle_id: doc.vehicle_id,
                vehicle_number: doc.vehicle_number,
                chassis_number: doc.chassis_number,
                alertStatus:
                  defaultStatus === "expiring"
                    ? doc.status === "critical"
                      ? "critical"
                      : "warning"
                    : "expired",
                documentStatuses: [],
              });
            }
            map.get(doc.vehicle_id).documentStatuses.push({
              name: doc.document_name,
              expiryDate: doc.expiry_date,
              daysRemaining: doc.days_left,
              status: doc.status,
            });
          });
          return Array.from(map.values());
        };

        setExpiringVehicles(buildMap(expiringDocs, "expiring"));
        setExpiredVehicles(buildMap(expiredDocs, "expired"));
      }
      await getVehicleDashboardStats();
    } catch {
      await fetchDocumentAlertsFallback();
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentAlertsFallback = async () => {
    try {
      const response = await vehicleList("", "", 1);
      let vehicles =
        response?.data?.results ||
        response?.results ||
        (Array.isArray(response) ? response : []);

      const expiring = [],
        expired = [];
      let expiringCount = 0,
        expiredCount = 0,
        criticalCount = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      vehicles.forEach((vehicle) => {
        const docs = [
          { name: "Insurance", date: vehicle.insurance_expiry, required: true },
          {
            name: "Pollution",
            date: vehicle.pollution_expiry,
            required: false,
          },
          { name: "Tax", date: vehicle.tax_validity, required: true },
          { name: "Fitness", date: vehicle.fitness_validity, required: true },
          {
            name: "National Permit",
            date: vehicle.national_permit,
            required: false,
          },
          { name: "CG Permit", date: vehicle.cg_permit, required: false },
        ];

        let hasExpiring = false,
          hasExpired = false,
          vehicleStatus = "good";
        const documentStatuses = [];

        docs.forEach((doc) => {
          if (doc.date) {
            const expiryDate = new Date(doc.date);
            expiryDate.setHours(0, 0, 0, 0);
            const daysRemaining = Math.ceil(
              (expiryDate - today) / (1000 * 60 * 60 * 24),
            );
            let status = "valid";
            if (expiryDate < today) {
              status = "expired";
              hasExpired = true;
              vehicleStatus = "expired";
              expiredCount++;
            } else if (daysRemaining <= 30) {
              status = "expiring";
              hasExpiring = true;
              if (vehicleStatus !== "expired")
                vehicleStatus = daysRemaining <= 7 ? "critical" : "warning";
              expiringCount++;
              if (daysRemaining <= 7) criticalCount++;
            }
            documentStatuses.push({
              ...doc,
              expiryDate: doc.date,
              daysRemaining: daysRemaining > 0 ? daysRemaining : 0,
              status,
            });
          } else if (doc.required) {
            documentStatuses.push({
              ...doc,
              expiryDate: null,
              daysRemaining: null,
              status: "missing",
            });
            hasExpired = true;
            vehicleStatus = "missing";
            expiredCount++;
          }
        });

        const ev = {
          ...vehicle,
          vehicle_id: vehicle.vehicle_id || vehicle.id,
          vehicle_number: vehicle.vehicle_number || vehicle.vehicleNumber,
          chassis_number: vehicle.chassis_number || vehicle.chassisNumber,
          documentStatuses,
          alertStatus: vehicleStatus,
        };
        if (hasExpired || vehicleStatus === "missing") expired.push(ev);
        else if (hasExpiring) expiring.push(ev);
      });

      setExpiringVehicles(expiring);
      setExpiredVehicles(expired);
      setStats({
        totalExpiring: expiringCount,
        totalExpired: expiredCount,
        criticalExpiring: criticalCount,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenAlertsModal = (type) => {
    setAlertsDocuments(
      type === "expiring" ? expiringDocuments : expiredDocuments,
    );
    setAlertsModalType(type);
    setAlertsModalOpen(true);
  };

  const filterVehicles = (list) => {
    let filtered = [...list];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (v) =>
          v.vehicle_number?.toLowerCase().includes(q) ||
          v.chassis_number?.toLowerCase().includes(q),
      );
    }
    if (filterType !== "all") {
      filtered = filtered.filter((v) => {
        if (filterType === "critical") return v.alertStatus === "critical";
        if (filterType === "warning") return v.alertStatus === "warning";
        if (filterType === "expired")
          return v.alertStatus === "expired" || v.alertStatus === "missing";
        return true;
      });
    }
    return filtered;
  };

  const handleViewDetails = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDetailsModalOpen(true);
  };
  const handleUpdate = (vehicle) =>
    navigate(`/dashboard/VehicleFrom/${vehicle.vehicle_id}`);

  const currentVehicles = filterVehicles(
    activeTab === 0 ? expiringVehicles : expiredVehicles,
  );
  const paginatedVehicles = currentVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1400, mx: "auto", width: "100%" }}
    >
      {/* ── Stats ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75}>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Expiring Soon"
              value={stats.totalExpiring}
              subtext={`${stats.criticalExpiring} Critical`}
              color="#d97706"
              onClick={() => handleOpenAlertsModal("expiring")}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatCard
              title="Expired Documents"
              value={stats.totalExpired}
              subtext="Action required"
              color={T.red}
              onClick={() => handleOpenAlertsModal("expired")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Alert
              severity="info"
              sx={{
                ...sq,
                boxShadow: "none",
                alignItems: "center",
                bgcolor: T.white,
                color: T.primary,
                py: 0.5,
                border: `1.5px solid ${T.border}`,
                "& .MuiAlert-message": { fontSize: "0.72rem", fontWeight: 500 },
                "& .MuiAlert-icon": { color: T.primary },
              }}
            >
              Keep all vehicle documents updated to avoid penalties.
            </Alert>
          </Grid>
        </Grid>
      </Box>

      {/* ── Tabs ── */}
      <Paper
        elevation={0}
        sx={{
          ...sq,
          border: `1.5px solid ${T.border}`,
          overflow: "hidden",
          mb: 1.5,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v);
            setPage(0);
          }}
          variant="fullWidth"
          sx={{
            minHeight: "auto",
            bgcolor: T.white,
            "& .MuiTabs-indicator": { bgcolor: T.primary, height: 2 },
          }}
        >
          {[
            `Expiring (${expiringVehicles.length})`,
            `Expired (${expiredVehicles.length})`,
          ].map((label, i) => (
            <Tab
              key={label}
              label={label}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.72rem",
                py: 0.75,
                minHeight: "auto",
                "&.Mui-selected": { color: T.primary },
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* ── Toolbar ── */}
      {/* ── Toolbar ── */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={5} md={4.5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by vehicle or chassis number…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 15, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
          </Grid>
          <Grid item xs={7} sm={4} md={3}>
            <FormControl size="small" fullWidth>
              <Select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(0);
                }}
                sx={{
                  height: 34,
                  ...sq,
                  bgcolor: T.white,
                  fontSize: "0.72rem",
                  "& fieldset": { border: `1.5px solid ${T.border}` },
                  "&:hover fieldset": {
                    border: `1.5px solid ${T.borderHover}`,
                  },
                  "&.Mui-focused fieldset": {
                    border: `1.5px solid ${T.primary}`,
                  },
                }}
              >
                {[
                  ["all", "All Alerts"],
                  ["critical", "Critical"],
                  ["warning", "Warning"],
                  ["expired", "Expired"],
                ].map(([val, lbl]) => (
                  <MenuItem key={val} value={val} sx={{ fontSize: "0.72rem" }}>
                    {lbl}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={5} sm={3} md={2}>
            <Button
              fullWidth
              size="small"
              onClick={() => {
                setSearchTerm("");
                setFilterType("all");
              }}
              sx={{ ...ghostBtn, height: 34 }}
            >
              Clear Filters
            </Button>
          </Grid>
          {/* Spacer */}
          <Grid
            item
            xs={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />
          {/* Alerts Count */}
          <Grid item xs={6} sm={6} md={1.5}>
            <Box
              sx={{
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                border: `1.5px solid ${T.border}`,
              }}
            >
              <AssignmentLate sx={{ fontSize: 15, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {currentVehicles.length} Alert
                {currentVehicles.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
          {/* Refresh Button - Now right of alerts count */}
          <Grid item xs={6} sm={6} md={0.75}>
            <Tooltip title="Refresh" arrow>
              <Button
                fullWidth
                size="small"
                onClick={fetchDocumentAlerts}
                sx={{ ...ghostBtn, height: 34, minWidth: 0, px: 1 }}
              >
                <Refresh sx={{ fontSize: 15 }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
      {/* ── Table / Cards ── */}
      {isMobile ? (
        loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : paginatedVehicles.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <CheckCircle sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No document alerts found
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub }}>
              All documents are up to date
            </Typography>
          </Box>
        ) : (
          <>
            {paginatedVehicles.map((vehicle) => (
              <MobileVehicleAlertCard
                key={vehicle.vehicle_id}
                vehicle={vehicle}
                expanded={expandedCardId === vehicle.vehicle_id}
                onToggle={() => handleCardToggle(vehicle.vehicle_id)}
                onUpdate={handleUpdate}
              />
            ))}
            {currentVehicles.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={currentVehicles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={paginationSx}
              />
            )}
          </>
        )
      ) : (
        <>
          <DesktopAlertsTable
            vehicles={paginatedVehicles}
            loading={loading}
            onViewDetails={handleViewDetails}
          />
          {currentVehicles.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${T.border}`,
                bgcolor: T.white,
                ...sq,
                border: `1.5px solid ${T.border}`,
                mt: 0,
                borderTop: "none",
              }}
            >
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={currentVehicles.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{
                  "& .MuiTablePagination-toolbar": { minHeight: 44, px: 2 },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: "0.7rem",
                      color: T.textSub,
                      fontWeight: 500,
                      margin: 0,
                    },
                  "& .MuiTablePagination-select": {
                    fontSize: "0.7rem",
                    ...sq,
                    border: `1px solid ${T.border}`,
                    bgcolor: T.white,
                    px: "5px",
                  },
                  "& .MuiTablePagination-actions button": { color: T.primary },
                  "& .MuiTablePagination-actions button:disabled": {
                    color: T.textMuted,
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* ── Modals ── */}
      <DocumentAlertsModal
        open={alertsModalOpen}
        onClose={() => setAlertsModalOpen(false)}
        title={
          alertsModalType === "expiring"
            ? "Expiring Soon Documents"
            : "Expired Documents"
        }
        documents={alertsDocuments}
        type={alertsModalType}
      />
      <DocumentDetailsModal
        open={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        vehicle={selectedVehicle}
        onUpdate={handleUpdate}
      />
    </Box>
  );
};

export default DocumentAlert;
